import { Box, Grid, Typography } from '@mui/material';
import { styles as styles } from '~/modules/identification/styles.ts';
import { commonStyles } from '~/theme/styles/commonStyles';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion.tsx';
import { informerUsEntityInfo } from '~/modules/identification/forms/FatcaAdditionalInformation/constants/informers.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import FormGridItem from '~/components/FormComponents/FormGridItem.tsx';
import { FormRadioGroup } from '~/components/FormComponents/FormRadioGroup.tsx';
import {
  fatcaTin,
  FatcaTinTypeOptions,
  REFUTATION_DOC_TYPE,
  refutationDocTypeOptions,
} from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { useSelector } from 'react-redux';
import { selectFullName } from '~/store/identification-form/selectors.ts';
import { useEffect } from 'react';
import { useUSACountryFlags } from '~/modules/identification/forms/GeneralInfo/hooks/useUSACountryFlags.ts';
import { infoMessages } from '~/constants/infoMessages.ts';
import { MultilineTextInput } from '~/components/TextField/MultilineTextInput.tsx';

export const FATCAFop = () => {
  const { setValue, control, unregister } = useFormContext();
  const { citizenshipCountryUSA, birthplaceCountryUSA, permanentAddressUSA } = useUSACountryFlags();
  const fullName = useSelector(selectFullName);
  const basePath = `fatca`;

  const [isForeignFinancialUnderFATCA, tinType, refutationDocType] = useWatch({
    name: [
      `${basePath}.isForeignFinancialUnderFATCA`,
      `${basePath}.tinType`,
      `${basePath}.refutationDocType`,
    ],
    control,
  });

  const isUSA = citizenshipCountryUSA || birthplaceCountryUSA || permanentAddressUSA;
  const isNativeCountryUSA = permanentAddressUSA || birthplaceCountryUSA;

  useEffect(() => {
    if (fullName) {
      setValue(`${basePath}.fullName`, fullName);
    }

    if (citizenshipCountryUSA) {
      setValue(`${basePath}.isForeignFinancialUnderFATCA`, true);
    }
  }, [fullName, citizenshipCountryUSA, isForeignFinancialUnderFATCA]);

  useEffect(() => {
    if (isForeignFinancialUnderFATCA === true) {
      unregister([`${basePath}.refutationDocType`, `${basePath}.explanation`]);
    }

    if (isForeignFinancialUnderFATCA === false) {
      unregister([`${basePath}.fullNameInEng`, `${basePath}.tinType`, `${basePath}.tin`]);

      if ([REFUTATION_DOC_TYPE.I_407, REFUTATION_DOC_TYPE.DS_4083].includes(refutationDocType)) {
        unregister([`${basePath}.explanation.usBirthAddress`, `${basePath}.explanation.message`]);
      }
    }
  }, [isForeignFinancialUnderFATCA, refutationDocType, basePath, unregister]);

  return (
    <Grid container spacing={3}>
      <Box sx={styles.displayFlex}>
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Інформація щодо приналежності до податкового резидентства США (FATCA)
        </Typography>
        <FormYesNoQuestion
          question={
            'Чи є Ви громадянином США або податковим резидентом США відповідно до законодавства США?'
          }
          tooltipTitle={informerUsEntityInfo}
          name={`${basePath}.isForeignFinancialUnderFATCA`}
          disabled={citizenshipCountryUSA}
        />
      </Box>
      {isForeignFinancialUnderFATCA ? (
        <>
          <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
            <Typography variant="body1">
              Вкажіть, будь ласка, ідентифікаційний номер платника податків США (TIN (SSN, ITIN)),
              FATCA-статус та найменування англійською мовою Вашої установи та надайте заповнену
              форму W-9
            </Typography>
            <InfoTooltip title={informerUsEntityInfo} />
          </Box>
          <FormGridItem>
            <FormInput
              name={`${basePath}.fullName`}
              label="Повне найменування клієнта"
              placeholder="Введіть повне найменування клієнта"
              disabled
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name={`${basePath}.fullNameInEng`}
              label="Прізвище, ім'я та по батькові (за наявності) англійською мовою"
              placeholder="Введіть прізвище, ім'я та по батькові (за наявності)"
              fullWidth
            />
          </FormGridItem>
          <FormRadioGroup
            name={`${basePath}.tinType`}
            options={FatcaTinTypeOptions}
            label={'Тип номера платника податків США'}
          />
          {fatcaTin.includes(tinType) && (
            <FormInput
              name={`${basePath}.tin`}
              label="Ідентифікаційний номер платника податків США (TIN (SSN, ITIN))"
              placeholder="Введіть ідентифікаційний номер платника податків США (TIN (SSN, ITIN))"
              fullWidth
            />
          )}
          <Typography variant="body2" sx={commonStyles.textInformer}>
            {infoMessages.FATCA_FOP_TRUE_INFORMER}
          </Typography>
        </>
      ) : isForeignFinancialUnderFATCA === false ? (
        <>
          <Typography variant="body2" sx={commonStyles.textInformer}>
            {infoMessages.FATCA_FOP_FALSE_INFORMER}
          </Typography>
          {isUSA && (
            <>
              <FormRadioGroup
                name={`${basePath}.refutationDocType`}
                label={
                  'В разі, якщо Вашим місцем народження є країна США, і Ви підтверджуєте, що не є ані громадянином США, ані податковим резидентом США, будь-ласка, надайте разом з цією Анкетою-опитувальником заповнену форму W-8BEN та один із документів'
                }
                options={refutationDocTypeOptions}
              />
              {refutationDocType === REFUTATION_DOC_TYPE.EXPLANATION && isNativeCountryUSA && (
                <>
                  {birthplaceCountryUSA && (
                    <MultilineTextInput
                      name={`${basePath}.explanation.usBirthAddress`}
                      label={'Адреса народження на території США'}
                      placeholder="Вкажіть адресу народження на території США"
                      textSize={1000}
                    />
                  )}
                  {(birthplaceCountryUSA || permanentAddressUSA) && (
                    <MultilineTextInput
                      name={`${basePath}.explanation.message`}
                      label={
                        'Уточніть, будь ласка, яка саме конкретна обставина (дипломатичний статус батьків, місце народження тощо) стосується вашого випадку? Вкажіть причини відсутності SSN/ITIN'
                      }
                      placeholder="Наприклад: народилась у США під час тимчасового перебування батьків, громадянство США не оформлювалось…"
                      textSize={1000}
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : null}
    </Grid>
  );
};
